import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Loads additional testimonial metaobject pages on demand.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLButtonElement} [loadMoreButton]
 * @property {HTMLElement} [status]
 *
 * @extends Component<Refs>
 */
class TestimonialsPageComponent extends Component {
  /** @type {boolean} */
  #isLoading = false;

  /**
   * @param {Event} event
   */
  async loadMore(event) {
    event.preventDefault();

    if (this.#isLoading) return;

    const { grid, loadMoreButton } = this.refs;

    if (!(grid instanceof HTMLUListElement) || !(loadMoreButton instanceof HTMLButtonElement)) return;

    const nextPage = Number(loadMoreButton.dataset.nextPage);
    const nextUrl = loadMoreButton.dataset.nextUrl;
    const lastPage = Number(grid.dataset.lastPage);
    const pageSize = Number(grid.dataset.pageSize);
    const totalCount = Number(grid.dataset.totalCount);

    if (!nextPage || !nextUrl || (lastPage && nextPage > lastPage)) {
      this.#removeLoadMore();
      return;
    }

    this.#setLoading(true);

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.hash = '';

      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      const parsedPage = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const nextGrid = parsedPage.querySelector('[ref="grid"]');

      if (!nextGrid) throw new Error('Testimonials grid not found in section response');

      const nextItems = Array.from(nextGrid.querySelectorAll(':scope > [ref="cards[]"]'));

      if (!nextItems.length) {
        this.#removeLoadMore();
        return;
      }

      const existingItemKeys = new Set(
        Array.from(grid.querySelectorAll('[ref="cards[]"]'))
          .map((item) => this.#getItemKey(item))
          .filter(Boolean)
      );
      const uniqueItems = nextItems.filter((item) => {
        const itemKey = this.#getItemKey(item);

        if (!itemKey || existingItemKeys.has(itemKey)) return false;

        existingItemKeys.add(itemKey);
        return true;
      });

      if (!uniqueItems.length) {
        this.#removeLoadMore();
        return;
      }

      grid.append(...uniqueItems);

      const nextButton = parsedPage.querySelector('[ref="loadMoreButton"]');
      const loadedItemsCount = grid.querySelectorAll('[ref="cards[]"]').length;
      const hasLoadedAllItems = totalCount > 0 && loadedItemsCount >= totalCount;
      const hasFullNextPage = !pageSize || uniqueItems.length >= pageSize;

      if (
        !hasLoadedAllItems &&
        hasFullNextPage &&
        nextButton instanceof HTMLButtonElement &&
        nextButton.dataset.nextPage &&
        nextButton.dataset.nextUrl &&
        Number(nextButton.dataset.nextPage) > nextPage
      ) {
        loadMoreButton.dataset.nextPage = nextButton.dataset.nextPage;
        loadMoreButton.dataset.nextUrl = nextButton.dataset.nextUrl;
        this.#setLoading(false);
        this.#announce(loadMoreButton.dataset.loadedMessage || '');
      } else {
        this.#removeLoadMore();
      }
    } catch (error) {
      console.error(error);
      this.#setLoading(false);
      this.#announce(loadMoreButton.dataset.errorMessage || '');
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { loadMoreButton } = this.refs;

    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    this.#isLoading = isLoading;
    loadMoreButton.disabled = isLoading;
    loadMoreButton.setAttribute('aria-busy', isLoading.toString());

    const label = isLoading ? loadMoreButton.dataset.loadingLabel : loadMoreButton.dataset.label;

    if (label) {
      loadMoreButton.textContent = label;
    }
  }

  #removeLoadMore() {
    const { loadMoreButton } = this.refs;

    loadMoreButton?.closest('.testimonials-page__load-more')?.remove();
    this.#announce('');
    this.#isLoading = false;
  }

  /**
   * @param {Element} item
   * @returns {string}
   */
  #getItemKey(item) {
    if (!(item instanceof HTMLElement)) return '';

    return item.dataset.testimonialId || item.id;
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const { status } = this.refs;

    if (status instanceof HTMLElement) {
      status.textContent = message;
    }
  }
}

if (!customElements.get('testimonials-page-component')) {
  customElements.define('testimonials-page-component', TestimonialsPageComponent);
}
